// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

// ---------------------------------------------------------------------------
// Primordia — Relationship Map
//
// MindMap.js draws the whole estate at once. This screen does the opposite: it
// answers "show me THIS and what it touches" and then puts that answer on paper.
// It is a client deliverable — an architecture relationship map that gets
// printed and handed to a health system — so legibility on A4/Letter matters
// more than on-screen prettiness.
//
// Three things this file refuses to do, because a deliverable that does them is
// worse than no deliverable:
//   1. Render a scope it has not counted first. Tenants here hold thousands of
//      fact sheets; the node/edge count is shown BEFORE anything is drawn and
//      anything over AUTO_RENDER_LIMIT needs an explicit "render anyway".
//   2. Crop silently. The PDF either scales the graph to one page and prints the
//      scale factor on the page, or tiles it across numbered pages at 100%.
//   3. Hide dangling relations. A relation pointing at a fact sheet that no
//      longer exists is a finding, not noise — it is counted and listed.
//
// Export stack: there is no jsPDF/html2canvas in package.json (checked), so the
// graph is serialised to plain SVG here and reused three ways —
//   PDF  → exports.printElementToPdf() over a hidden print block (landscape
//          @page injected with the block, so the shared helper still does the work)
//   PNG  → SVG → <img> → <canvas> → exports.exportCanvasPng()
//   CSV  → exports.exportCsv() over the edge list
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, { Background, Controls, MarkerType, Position } from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Alert, AlertTitle, Autocomplete, Box, Button, Chip, CircularProgress, Divider,
  MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DownloadIcon from '@mui/icons-material/Download';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import HubIcon from '@mui/icons-material/Hub';
import { FACT_SHEET_TYPES, relationMeta, typeMeta } from './schema';
import { exportCanvasPng, exportCsv, printElementToPdf } from './exports';
import PrimordiaLogo from './PrimordiaLogo';
import store from './store';

// ---- Optimum palette -------------------------------------------------------
const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';

// ---- Render guards ---------------------------------------------------------
const AUTO_RENDER_LIMIT = 300;   // above this we count, warn, and wait for a click
const EDGE_LABEL_LIMIT = 120;    // above this, edge labels stop being readable
const VIRTUALISE_ABOVE = 150;    // ReactFlow onlyRenderVisibleElements
const EDGE_TABLE_LIMIT = 200;    // rows of edge list printed into the PDF

// ---- Geometry (shared by the ReactFlow canvas and the exported SVG) --------
const NODE_W = 184;
const NODE_H = 46;
const COL_GAP = 118;             // layered: horizontal gap between ranks
const ROW_GAP = 24;              // layered: vertical gap inside a rank

// Printable area, laid out for Letter landscape with 10mm margins (10.2in x
// 7.7in ≈ 979 x 739 CSS px). Deliberately the smaller of Letter/A4 landscape so
// the same PDF prints without cropping on either. MAP_H leaves room for the
// per-page caption that keeps every sheet self-identifying.
const PAGE_W = 970;
const PAGE_H = 715;
const MAP_H = PAGE_H - 26;

const PRINT_ID = 'relationmap-print';

// ---------------------------------------------------------------------------
// Relation direction.
//
// Several relation keys are two recordings of ONE link, stored from each end
// (a Deployment carries `application`, the Application carries `deployments`).
// Drawing both would double the edge count and invent a cycle that is not in
// the data, so each inverse key is folded onto its canonical direction. Nothing
// is added here that the data does not already state — only the arrow's sense.
// ---------------------------------------------------------------------------
const INVERSE_OF = {
  application: 'deployments',        // Deployment → its App   ⇒ Application → Deployment
  hostedDeployments: 'servers',      // ITComponent → Deployment ⇒ Deployment → ITComponent
  parent: 'children',                // child → parent          ⇒ parent → child
  predecessors: 'successors',        // fs → predecessor        ⇒ predecessor → fs
};

// The Application → Deployment → ITComponent chain: the newest and most load-
// bearing path in the model, drawn heavier so it survives a printer.
const CHAIN_KEYS = new Set(['deployments', 'servers']);

const TYPE_ORDER = new Map(FACT_SHEET_TYPES.map((t, i) => [t.key, i]));
const typeRank = (t) => (TYPE_ORDER.has(t) ? TYPE_ORDER.get(t) : 99);
const relLabel = (key) => (relationMeta(key) || {}).label || key;

const CHAIN_COLOR = '#0D47A1';
const PLAIN_COLOR = '#96A3B1';

const nowStamp = () => new Date().toLocaleString();
const fileStamp = () => new Date().toISOString().slice(0, 10);

// ---------------------------------------------------------------------------
// Saved-view matching.
//
// Saved views are persisted by SavedViews.js under `primordia.views.<tenantId>`.
// Its evaluator is module-private there, so the subset this screen needs (type
// + filters) is mirrored below rather than exported — SavedViews.js is not ours
// to edit. Semantics kept identical, including "a half-built filter row is
// inactive".
// ---------------------------------------------------------------------------
const VALUELESS_OPS = ['is empty', 'is not empty'];

function viewValueOf(fs, key) {
  if (!key) return undefined;
  if (key === '__name') return fs.name;
  if (key === '__type') return fs.type;
  if (key === '__completion') return fs.completion;
  if (key === '__quality') return fs.quality;
  if (key.indexOf('__tag:') === 0) {
    const group = key.slice(6);
    return (fs.tags || []).filter((t) => t.group === group).map((t) => t.value).join(', ');
  }
  const v = fs.fields ? fs.fields[key] : undefined;
  if ((v === undefined || v === null || v === '') && key === 'name') return fs.name;
  return v;
}

function viewDisplay(v) {
  if (v === undefined || v === null || v === '') return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.join(', ');
  return String(v);
}

function matchesViewFilter(fs, f) {
  if (!f || !f.field || !f.op) return true;
  const raw = viewValueOf(fs, f.field);
  const s = viewDisplay(raw);
  const target = (f.value === undefined || f.value === null) ? '' : String(f.value);
  if (VALUELESS_OPS.indexOf(f.op) === -1 && target === '') return true;
  switch (f.op) {
    case 'is': return s.toLowerCase() === target.toLowerCase();
    case 'is not': return s.toLowerCase() !== target.toLowerCase();
    case 'contains': return s.toLowerCase().indexOf(target.toLowerCase()) !== -1;
    case 'is empty': return s === '';
    case 'is not empty': return s !== '';
    case 'greater than': {
      const a = Number(raw);
      const b = Number(target);
      return (Number.isFinite(a) && Number.isFinite(b)) ? a > b : s.localeCompare(target) > 0;
    }
    case 'less than': {
      const a = Number(raw);
      const b = Number(target);
      return (Number.isFinite(a) && Number.isFinite(b)) ? a < b : s.localeCompare(target) < 0;
    }
    default: return true;
  }
}

const matchesView = (fs, view) => {
  if (!view) return false;
  if (view.type && fs.type !== view.type) return false;
  return (view.filters || []).every((f) => matchesViewFilter(fs, f));
};

// ---------------------------------------------------------------------------
// Layouts. Both are deterministic — the same scope always yields the same
// picture, which matters when the same map is re-issued to a client.
// ---------------------------------------------------------------------------

// Hierarchical / layered: rank by longest directed path from the sources, so
// Application (rank 0) → Deployment (1) → ITComponent (2) falls out naturally.
// Cycles cannot hang it: Kahn's algorithm drains what it can, and whatever is
// left in a cycle keeps the highest rank it reached.
function layeredPositions(nodes, edges) {
  const ids = nodes.map((n) => n.id);
  const idSet = new Set(ids);
  const out = new Map(ids.map((id) => [id, []]));
  const indeg = new Map(ids.map((id) => [id, 0]));
  edges.forEach((e) => {
    if (!idSet.has(e.source) || !idSet.has(e.target)) return;
    out.get(e.source).push(e.target);
    indeg.set(e.target, indeg.get(e.target) + 1);
  });

  const rank = new Map(ids.map((id) => [id, 0]));
  const queue = ids.filter((id) => indeg.get(id) === 0);
  let head = 0;
  while (head < queue.length) {
    const id = queue[head];
    head += 1;
    out.get(id).forEach((t) => {
      if (rank.get(t) < rank.get(id) + 1) rank.set(t, rank.get(id) + 1);
      indeg.set(t, indeg.get(t) - 1);
      if (indeg.get(t) === 0) queue.push(t);
    });
  }

  const byRank = new Map();
  nodes.forEach((n) => {
    const r = rank.get(n.id) || 0;
    if (!byRank.has(r)) byRank.set(r, []);
    byRank.get(r).push(n);
  });

  const pos = new Map();
  const ranks = [...byRank.keys()].sort((a, b) => a - b);
  const tallest = Math.max(...ranks.map((r) => byRank.get(r).length), 1);
  const colHeight = tallest * (NODE_H + ROW_GAP);
  ranks.forEach((r, colIdx) => {
    const col = byRank.get(r).slice().sort((a, b) => {
      const d = typeRank(a.type) - typeRank(b.type);
      return d !== 0 ? d : String(a.name).localeCompare(String(b.name));
    });
    const span = col.length * (NODE_H + ROW_GAP);
    const top = (colHeight - span) / 2;
    col.forEach((n, i) => {
      pos.set(n.id, { x: colIdx * (NODE_W + COL_GAP), y: top + i * (NODE_H + ROW_GAP) });
    });
  });
  return pos;
}

// Radial: seeds in the middle, one ring per degree of separation. Hub-and-spoke
// estates (one Epic, forty components) read far better this way than layered.
function radialPositions(nodes, depthOf) {
  const byRing = new Map();
  nodes.forEach((n) => {
    const d = depthOf.get(n.id) || 0;
    if (!byRing.has(d)) byRing.set(d, []);
    byRing.get(d).push(n);
  });

  const pos = new Map();
  const rings = [...byRing.keys()].sort((a, b) => a - b);
  let prevR = 0;
  rings.forEach((ring) => {
    const group = byRing.get(ring).slice().sort((a, b) => {
      const d = typeRank(a.type) - typeRank(b.type);
      return d !== 0 ? d : String(a.name).localeCompare(String(b.name));
    });
    if (ring === 0 && group.length === 1) {
      pos.set(group[0].id, { x: -NODE_W / 2, y: -NODE_H / 2 });
      return;
    }
    // Ring radius is driven by what the ring actually has to hold, with a floor
    // that keeps consecutive rings apart. A fixed radius-per-degree wastes half
    // the page on a sparse map, and a printed map has no zoom control.
    const needed = (group.length * (NODE_W + 46)) / (2 * Math.PI);
    const radius = Math.max(needed, prevR + 220, ring === 0 ? 130 : 220);
    prevR = radius;
    group.forEach((n, i) => {
      const a = (i / group.length) * 2 * Math.PI - Math.PI / 2;
      pos.set(n.id, {
        x: Math.cos(a) * radius * 1.35 - NODE_W / 2,
        y: Math.sin(a) * radius - NODE_H / 2,
      });
    });
  });
  return pos;
}

// ---------------------------------------------------------------------------
// SVG serialisation — one drawing routine feeding both the PDF and the PNG.
// No foreignObject, no external fonts, no web requests: it has to rasterise on
// a canvas and survive a print dialog.
// ---------------------------------------------------------------------------
const FONT = "-apple-system, 'Segoe UI', Roboto, Arial, sans-serif";

function svgEsc(s) {
  return String(s === undefined || s === null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function wrapLabel(name, perLine, maxLines) {
  const words = String(name || '').split(/\s+/).filter(Boolean);
  const packed = [];
  let cur = '';
  words.forEach((w) => {
    if (!cur) { cur = w; return; }
    if ((`${cur} ${w}`).length <= perLine) cur = `${cur} ${w}`;
    else { packed.push(cur); cur = w; }
  });
  if (cur) packed.push(cur);

  const lines = [];
  packed.forEach((l) => {
    let s = l;
    while (s.length > perLine) { lines.push(s.slice(0, perLine)); s = s.slice(perLine); }
    if (s) lines.push(s);
  });
  if (!lines.length) return [''];
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = `${kept[maxLines - 1].slice(0, Math.max(1, perLine - 1))}…`;
    return kept;
  }
  return lines;
}

// Where a straight line from a box centre leaves that box.
function boxExit(cx, cy, tx, ty) {
  const dx = tx - cx;
  const dy = ty - cy;
  if (dx === 0 && dy === 0) return { x: cx, y: cy };
  const sx = dx === 0 ? Infinity : (NODE_W / 2) / Math.abs(dx);
  const sy = dy === 0 ? Infinity : (NODE_H / 2) / Math.abs(dy);
  const s = Math.min(sx, sy);
  return { x: cx + dx * s, y: cy + dy * s };
}

function svgNode(n) {
  const m = typeMeta(n.type);
  const fill = n.seed ? m.color : '#ffffff';
  const nameFill = n.seed ? '#ffffff' : NAVY;
  const badgeFill = n.seed ? '#ffffff' : m.color;
  const badgeText = n.seed ? m.color : '#ffffff';
  const lines = wrapLabel(n.name, 24, 2);
  const startY = lines.length === 1 ? n.y + 28 : n.y + 21;
  const text = lines
    .map((l, i) => `<text x="${n.x + 40}" y="${startY + i * 12.5}" font-family="${FONT}" font-size="10.5" font-weight="${n.seed ? 700 : 500}" fill="${nameFill}">${svgEsc(l)}</text>`)
    .join('');
  return [
    `<g><rect x="${n.x}" y="${n.y}" width="${NODE_W}" height="${NODE_H}" rx="8" fill="${fill}" stroke="${m.color}" stroke-width="2"/>`,
    `<rect x="${n.x + 8}" y="${n.y + 15}" width="24" height="16" rx="3.5" fill="${badgeFill}"/>`,
    `<text x="${n.x + 20}" y="${n.y + 26.5}" text-anchor="middle" font-family="${FONT}" font-size="9.5" font-weight="700" fill="${badgeText}">${svgEsc(m.badge)}</text>`,
    text,
    '</g>',
  ].join('');
}

function svgEdge(e, posOf, withLabel) {
  const a = posOf.get(e.source);
  const b = posOf.get(e.target);
  if (!a || !b) return '';
  const ac = { x: a.x + NODE_W / 2, y: a.y + NODE_H / 2 };
  const bc = { x: b.x + NODE_W / 2, y: b.y + NODE_H / 2 };
  const p1 = boxExit(ac.x, ac.y, bc.x, bc.y);
  const p2 = boxExit(bc.x, bc.y, ac.x, ac.y);
  const color = e.chain ? CHAIN_COLOR : PLAIN_COLOR;
  const marker = e.chain ? 'url(#rmArrowChain)' : 'url(#rmArrowPlain)';
  const line = `<line x1="${p1.x.toFixed(1)}" y1="${p1.y.toFixed(1)}" x2="${p2.x.toFixed(1)}" y2="${p2.y.toFixed(1)}" stroke="${color}" stroke-width="${e.chain ? 2.2 : 1.1}" marker-end="${marker}"/>`;
  if (!withLabel || !e.label) return line;
  const mx = (p1.x + p2.x) / 2;
  const my = (p1.y + p2.y) / 2;
  const w = e.label.length * 5.1 + 8;
  return [
    line,
    `<rect x="${(mx - w / 2).toFixed(1)}" y="${(my - 7).toFixed(1)}" width="${w.toFixed(1)}" height="13" rx="3" fill="#ffffff" fill-opacity="0.88"/>`,
    `<text x="${mx.toFixed(1)}" y="${(my + 2.5).toFixed(1)}" text-anchor="middle" font-family="${FONT}" font-size="9" fill="${e.chain ? CHAIN_COLOR : GREY}">${svgEsc(e.label)}</text>`,
  ].join('');
}

// Legend block, drawn into the SVG so a PNG also stands alone once it leaves
// the room. The PDF gets the same legend as HTML (crisper on a printer).
function svgLegend(x, y, width, typeRows) {
  const cols = Math.max(1, Math.floor(width / 215));
  const rows = Math.ceil(typeRows.length / cols);
  const parts = [
    `<text x="${x}" y="${y + 12}" font-family="${FONT}" font-size="12" font-weight="700" fill="${NAVY}">Legend</text>`,
  ];
  typeRows.forEach((t, i) => {
    const c = i % cols;
    const r = Math.floor(i / cols);
    const bx = x + c * 215;
    const by = y + 26 + r * 20;
    parts.push(`<rect x="${bx}" y="${by}" width="22" height="14" rx="3.5" fill="${t.color}"/>`);
    parts.push(`<text x="${bx + 11}" y="${by + 10.5}" text-anchor="middle" font-family="${FONT}" font-size="9" font-weight="700" fill="#ffffff">${svgEsc(t.badge)}</text>`);
    parts.push(`<text x="${bx + 28}" y="${by + 11}" font-family="${FONT}" font-size="10.5" fill="${NAVY}">${svgEsc(`${t.label} (${t.count})`)}</text>`);
  });

  const ey = y + 26 + rows * 20 + 10;
  const edgeRows = [
    { color: CHAIN_COLOR, wdt: 2.2, text: 'Application → Deployment → IT Component (deployment chain)' },
    { color: PLAIN_COLOR, wdt: 1.1, text: 'Other model relation, labelled with its relation type' },
  ];
  edgeRows.forEach((er, i) => {
    const by = ey + i * 18;
    parts.push(`<line x1="${x}" y1="${by}" x2="${x + 34}" y2="${by}" stroke="${er.color}" stroke-width="${er.wdt}" marker-end="url(#rmArrow${er.color === CHAIN_COLOR ? 'Chain' : 'Plain'})"/>`);
    parts.push(`<text x="${x + 44}" y="${by + 3.5}" font-family="${FONT}" font-size="10.5" fill="${NAVY}">${svgEsc(er.text)}</text>`);
  });
  parts.push(`<text x="${x}" y="${ey + edgeRows.length * 18 + 4}" font-family="${FONT}" font-size="10" fill="${GREY}">Arrows show the direction the relation is recorded in.</text>`);
  parts.push(`<rect x="${x}" y="${ey + edgeRows.length * 18 + 14}" width="22" height="14" rx="3.5" fill="${NAVY}"/>`);
  parts.push(`<text x="${x + 28}" y="${ey + edgeRows.length * 18 + 25}" font-family="${FONT}" font-size="10" fill="${GREY}">filled = a fact sheet the scope started from  ·  outlined = reached by traversal</text>`);

  return { markup: parts.join(''), height: ey + edgeRows.length * 18 + 40 - y };
}

// Builds the SVG body + its natural size. `header` and `legend` are opt-in so
// the PDF (which has its own HTML title block and legend) and the PNG (which
// must stand alone) can share one drawing routine.
function buildSvg(model, opts) {
  const o = opts || {};
  const pad = 26;
  const nodes = model.nodes;
  let minX = 0;
  let minY = 0;
  let maxX = NODE_W;
  let maxY = NODE_H;
  if (nodes.length) {
    minX = Math.min(...nodes.map((n) => n.x));
    minY = Math.min(...nodes.map((n) => n.y));
    maxX = Math.max(...nodes.map((n) => n.x + NODE_W));
    maxY = Math.max(...nodes.map((n) => n.y + NODE_H));
  }
  const gW = maxX - minX;
  const gH = maxY - minY;

  const headerH = o.header ? 62 : 0;
  const contentW = Math.max(gW, o.header || o.legend ? 700 : 0);

  const posOf = new Map(nodes.map((n) => [n.id, { x: n.x - minX, y: n.y - minY }]));
  const drawNodes = nodes.map((n) => ({ ...n, ...posOf.get(n.id) }));

  const body = [];
  body.push('<rect x="0" y="0" width="__W__" height="__H__" fill="#ffffff"/>');

  if (o.header) {
    const meta = o.meta || {};
    body.push(`<text x="${pad}" y="${pad + 6}" font-family="${FONT}" font-size="16" font-weight="700" fill="${NAVY}">${svgEsc(meta.title || 'Primordia — Relationship Map')}</text>`);
    body.push(`<text x="${pad}" y="${pad + 25}" font-family="${FONT}" font-size="11.5" fill="${BLUE}">${svgEsc(meta.scope || '')}</text>`);
    body.push(`<text x="${pad}" y="${pad + 42}" font-family="${FONT}" font-size="10.5" fill="${GREY}">${svgEsc(meta.sub || '')}</text>`);
  }

  body.push(`<g transform="translate(${pad},${pad + headerH})">`);
  model.edges.forEach((e) => body.push(svgEdge(e, posOf, o.labels)));
  drawNodes.forEach((n) => body.push(svgNode(n)));
  body.push('</g>');

  let legendH = 0;
  if (o.legend) {
    const lg = svgLegend(pad, pad + headerH + gH + 24, Math.max(contentW, 640), o.typeRows || []);
    legendH = lg.height + 24;
    body.push(lg.markup);
  }

  const width = Math.round(Math.max(contentW, 640) + pad * 2);
  const height = Math.round(gH + headerH + legendH + pad * 2);

  const defs = [
    '<defs>',
    `<marker id="rmArrowChain" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="${CHAIN_COLOR}"/></marker>`,
    `<marker id="rmArrowPlain" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M0,0 L10,5 L0,10 z" fill="${PLAIN_COLOR}"/></marker>`,
    '</defs>',
  ].join('');

  const inner = defs + body.join('').replace('__W__', String(width)).replace('__H__', String(height));
  return { inner, width, height, graphWidth: Math.round(gW + pad * 2), graphHeight: Math.round(gH + pad * 2) };
}

const svgDoc = (svg, renderW, renderH) =>
  `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${svg.width} ${svg.height}" width="${Math.round(renderW)}" height="${Math.round(renderH)}">${svg.inner}</svg>`;

// Print CSS travels inside the printed block, so exports.js keeps owning the
// print window and we still get landscape paper.
const PRINT_CSS = `
  @page { size: landscape; margin: 10mm; }
  body { margin: 0 !important; font-family: ${FONT}; color: #16202c; }
  .rm-title { display: flex; align-items: center; gap: 10px; border-bottom: 2px solid ${NAVY}; padding-bottom: 8px; margin-bottom: 10px; }
  .rm-title h1 { font-size: 17px; margin: 0; color: ${NAVY}; }
  .rm-title .sub { font-size: 11.5px; color: ${BLUE}; margin-top: 2px; }
  .rm-meta { display: flex; flex-wrap: wrap; gap: 4px 22px; font-size: 10.5px; color: ${GREY}; margin-bottom: 10px; }
  .rm-meta b { color: ${NAVY}; }
  .rm-note { font-size: 10.5px; color: #8a5a00; background: #fff8e6; border: 1px solid #f0d9a8; padding: 6px 8px; border-radius: 4px; margin-bottom: 10px; }
  .rm-legend { border: 1px solid #d7dee6; border-radius: 4px; padding: 8px 10px; margin: 10px 0; font-size: 10.5px; page-break-inside: avoid; }
  .rm-legend h2 { font-size: 12px; margin: 0 0 6px; color: ${NAVY}; }
  .rm-legend .items { display: flex; flex-wrap: wrap; gap: 4px 16px; }
  .rm-legend .it { display: flex; align-items: center; gap: 6px; width: 200px; }
  .rm-legend .bg { display: inline-block; width: 22px; height: 14px; border-radius: 3px; color: #fff; font-size: 9px; font-weight: 700; text-align: center; line-height: 14px; }
  .rm-legend .rule { display: inline-block; width: 34px; vertical-align: middle; }
  .rm-page { page-break-after: always; }
  .rm-page:last-child { page-break-after: auto; }
  .rm-mappage { page-break-before: always; }
  .rm-tile { overflow: hidden; position: relative; }
  .rm-tile > div { position: absolute; }
  .rm-cap { font-size: 10px; color: ${GREY}; margin-top: 4px; }
  table.rm-edges { border-collapse: collapse; width: 100%; font-size: 10px; }
  table.rm-edges th { text-align: left; color: ${NAVY}; border-bottom: 1px solid #c8d2dd; padding: 3px 6px; }
  table.rm-edges td { border-bottom: 1px solid #eef1f5; padding: 3px 6px; }
`;

// ---------------------------------------------------------------------------
export default function RelationMap({ tenantId, onOpen }) {
  const [sheets, setSheets] = useState(null);
  const [tenantName, setTenantName] = useState(tenantId);
  const [savedViews, setSavedViews] = useState([]);

  const [mode, setMode] = useState('sheet');       // sheet | type | view
  const [focusId, setFocusId] = useState('');
  const [scopeType, setScopeType] = useState('Application');
  const [viewId, setViewId] = useState('');
  const [degrees, setDegrees] = useState(1);
  const [layout, setLayout] = useState('layered'); // layered | radial
  const [pageMode, setPageMode] = useState('fit'); // fit | tile
  const [force, setForce] = useState(false);

  // ---- Load -----------------------------------------------------------------
  useEffect(() => {
    let live = true;
    setSheets(null);
    store.listFactSheets(tenantId).then((rows) => { if (live) setSheets(rows || []); });
    return () => { live = false; };
  }, [tenantId]);

  useEffect(() => {
    let live = true;
    store.listTenants().then((list) => {
      if (!live) return;
      const t = (list || []).find((x) => x.id === tenantId);
      setTenantName(t ? t.name : tenantId);
    });
    return () => { live = false; };
  }, [tenantId]);

  useEffect(() => {
    let list = [];
    try {
      const raw = localStorage.getItem(`primordia.views.${tenantId}`);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed)) list = parsed.filter((v) => v && (!v.kind || v.kind === 'view'));
    } catch (e) { /* unreadable local views are simply "none saved" */ }
    setSavedViews(list);
    setViewId('');
    setFocusId('');
    setMode('sheet');
  }, [tenantId]);

  // Any scope change invalidates a previous "render anyway".
  useEffect(() => { setForce(false); }, [mode, focusId, scopeType, viewId, degrees]);

  // ---- Whole-tenant graph (built once per tenant) ---------------------------
  const full = useMemo(() => {
    const rows = sheets || [];
    const byId = new Map(rows.map((fs) => [fs.id, fs]));
    const edges = [];
    const seen = new Set();
    const dangling = [];
    const danglingSeen = new Set();

    rows.forEach((fs) => {
      Object.entries(fs.relations || {}).forEach(([relKey, arr]) => {
        (arr || []).forEach((r) => {
          if (!r || !r.id) return;
          if (!byId.has(r.id)) {
            const dk = `${fs.id}|${relKey}|${r.id}`;
            if (!danglingSeen.has(dk)) {
              danglingSeen.add(dk);
              dangling.push({
                key: dk,
                sourceId: fs.id,
                sourceName: fs.name,
                sourceType: fs.type,
                relKey,
                missingId: r.id,
                missingName: r.name || '',
              });
            }
            return;
          }
          if (r.id === fs.id) return;                       // self-links add nothing
          const canonical = INVERSE_OF[relKey] || relKey;
          const source = INVERSE_OF[relKey] ? r.id : fs.id;
          const target = INVERSE_OF[relKey] ? fs.id : r.id;
          const dedupe = `${source}|${target}|${canonical}`;
          if (seen.has(dedupe)) return;
          seen.add(dedupe);
          edges.push({ id: `e${edges.length}`, source, target, relKey: canonical });
        });
      });
    });

    const adj = new Map();
    const link = (a, b) => {
      if (!adj.has(a)) adj.set(a, []);
      adj.get(a).push(b);
    };
    edges.forEach((e) => { link(e.source, e.target); link(e.target, e.source); });

    const relCounts = new Map();
    edges.forEach((e) => relCounts.set(e.relKey, (relCounts.get(e.relKey) || 0) + 1));

    return { rows, byId, edges, adj, dangling, relCounts };
  }, [sheets]);

  const focusSheet = focusId ? full.byId.get(focusId) : null;

  // ---- Scope: seeds + N degrees of separation --------------------------------
  const scope = useMemo(() => {
    const empty = {
      nodeIds: [], edges: [], seeds: new Set(), depth: new Map(),
      description: 'No scope selected', seedCount: 0, ready: false,
    };
    if (!sheets) return empty;

    let seedRows = [];
    let description = '';
    if (mode === 'sheet') {
      const fs = focusId ? full.byId.get(focusId) : null;
      if (!fs) return { ...empty, description: 'Pick a fact sheet to focus on' };
      seedRows = [fs];
      description = `${fs.name} (${typeMeta(fs.type).label}), ${degrees} degree${degrees === 1 ? '' : 's'}`;
    } else if (mode === 'type') {
      seedRows = full.rows.filter((fs) => fs.type === scopeType);
      description = `All ${typeMeta(scopeType).label} fact sheets (${seedRows.length}), ${degrees} degree${degrees === 1 ? '' : 's'}`;
    } else {
      const view = savedViews.find((v) => v.id === viewId);
      if (!view) return { ...empty, description: 'Pick a saved view' };
      seedRows = full.rows.filter((fs) => matchesView(fs, view));
      description = `Saved view "${view.name}" (${seedRows.length} fact sheets), ${degrees} degree${degrees === 1 ? '' : 's'}`;
    }

    const seeds = new Set(seedRows.map((fs) => fs.id));
    const depth = new Map(seedRows.map((fs) => [fs.id, 0]));
    let frontier = [...seeds];
    for (let d = 1; d <= degrees; d += 1) {
      const next = [];
      frontier.forEach((id) => {
        (full.adj.get(id) || []).forEach((nb) => {
          if (depth.has(nb)) return;
          depth.set(nb, d);
          next.push(nb);
        });
      });
      frontier = next;
      if (!frontier.length) break;
    }

    const nodeIds = [...depth.keys()].sort((a, b) => {
      const fa = full.byId.get(a);
      const fb = full.byId.get(b);
      const d = typeRank(fa.type) - typeRank(fb.type);
      return d !== 0 ? d : String(fa.name).localeCompare(String(fb.name));
    });
    const inScope = new Set(nodeIds);
    const edges = full.edges.filter((e) => inScope.has(e.source) && inScope.has(e.target));

    return {
      nodeIds, edges, seeds, depth, description,
      seedCount: seeds.size, ready: true, inScope,
    };
  }, [sheets, full, mode, focusId, scopeType, viewId, degrees, savedViews]);

  const overLimit = scope.nodeIds.length > AUTO_RENDER_LIMIT;
  const canRender = scope.ready && scope.nodeIds.length > 0 && scope.edges.length > 0 && (!overLimit || force);

  // Dangling relations whose SOURCE sits inside the current scope — a real
  // data-quality finding, surfaced rather than swallowed.
  const danglingInScope = useMemo(() => {
    if (!scope.inScope) return [];
    return full.dangling.filter((d) => scope.inScope.has(d.sourceId));
  }, [full, scope]);

  // ---- Positioned graph model ------------------------------------------------
  const graph = useMemo(() => {
    if (!canRender) return null;
    const nodes = scope.nodeIds.map((id) => full.byId.get(id)).filter(Boolean);
    const pos = layout === 'radial'
      ? radialPositions(nodes.map((fs) => ({ id: fs.id, name: fs.name, type: fs.type })), scope.depth)
      : layeredPositions(nodes.map((fs) => ({ id: fs.id, name: fs.name, type: fs.type })), scope.edges);

    const modelNodes = nodes.map((fs) => {
      const p = pos.get(fs.id) || { x: 0, y: 0 };
      return {
        id: fs.id, name: fs.name, type: fs.type,
        seed: scope.seeds.has(fs.id), x: p.x, y: p.y,
      };
    });
    const modelEdges = scope.edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      relKey: e.relKey,
      label: relLabel(e.relKey),
      chain: CHAIN_KEYS.has(e.relKey),
    }));

    const typeCounts = new Map();
    modelNodes.forEach((n) => typeCounts.set(n.type, (typeCounts.get(n.type) || 0) + 1));
    const typeRows = [...typeCounts.keys()]
      .sort((a, b) => typeRank(a) - typeRank(b))
      .map((t) => ({ ...typeMeta(t), count: typeCounts.get(t) }));

    return { nodes: modelNodes, edges: modelEdges, typeRows, labels: modelEdges.length <= EDGE_LABEL_LIMIT };
  }, [canRender, scope, full, layout]);

  // ---- ReactFlow view --------------------------------------------------------
  const flow = useMemo(() => {
    if (!graph) return { nodes: [], edges: [] };
    const nodes = graph.nodes.map((n) => {
      const m = typeMeta(n.type);
      return {
        id: n.id,
        position: { x: n.x, y: n.y },
        data: { label: `${m.badge} · ${n.name}` },
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        style: {
          background: n.seed ? m.color : '#fff',
          color: n.seed ? '#fff' : NAVY,
          border: `2px solid ${m.color}`,
          borderRadius: 8,
          width: NODE_W,
          fontSize: 11,
          fontWeight: n.seed ? 700 : 500,
          padding: '6px 8px',
          cursor: 'pointer',
        },
      };
    });
    const edges = graph.edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      type: layout === 'layered' ? 'smoothstep' : 'straight',
      label: graph.labels ? e.label : undefined,
      labelStyle: { fontSize: 9, fill: e.chain ? CHAIN_COLOR : GREY },
      labelBgStyle: { fill: '#fff', fillOpacity: 0.85 },
      markerEnd: { type: MarkerType.ArrowClosed, color: e.chain ? CHAIN_COLOR : PLAIN_COLOR },
      style: { stroke: e.chain ? CHAIN_COLOR : PLAIN_COLOR, strokeWidth: e.chain ? 2 : 1 },
    }));
    return { nodes, edges };
  }, [graph, layout]);

  // ---- Export payloads -------------------------------------------------------
  const printSvg = useMemo(
    () => (graph ? buildSvg(graph, { labels: graph.labels }) : null),
    [graph]
  );

  const fit = useMemo(() => {
    if (!printSvg) return null;
    const s = Math.min(1, PAGE_W / printSvg.width, MAP_H / printSvg.height);
    return {
      scale: s,
      pct: Math.max(1, Math.round(s * 100)),
      cols: Math.ceil(printSvg.width / PAGE_W),
      rows: Math.ceil(printSvg.height / MAP_H),
    };
  }, [printSvg]);

  const edgeRows = useMemo(() => {
    if (!graph) return [];
    return graph.edges.map((e) => {
      const s = full.byId.get(e.source);
      const t = full.byId.get(e.target);
      return {
        id: e.id,
        source: s ? s.name : e.source,
        sourceType: s ? typeMeta(s.type).label : '',
        relation: e.label,
        target: t ? t.name : e.target,
        targetType: t ? typeMeta(t.type).label : '',
      };
    }).sort((a, b) => a.source.localeCompare(b.source) || a.relation.localeCompare(b.relation) || a.target.localeCompare(b.target));
  }, [graph, full]);

  const scopeSub = graph
    ? `${graph.nodes.length} fact sheets · ${graph.edges.length} relations · ${layout === 'layered' ? 'hierarchical' : 'radial'} layout · generated ${nowStamp()}`
    : '';

  const doPng = useCallback(() => {
    if (!graph) return;
    const svg = buildSvg(graph, {
      labels: graph.labels,
      header: true,
      legend: true,
      typeRows: graph.typeRows,
      meta: {
        title: `Primordia — Relationship Map · ${tenantName}`,
        scope: scope.description,
        sub: scopeSub,
      },
    });
    const markup = svgDoc(svg, svg.width, svg.height);
    const blob = new Blob([markup], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      // 2x for a crisp raster, clamped so a "render anyway" monster graph cannot
      // exceed the browser's canvas limits and fail silently.
      const px = Math.min(2, 16000 / Math.max(svg.width, svg.height, 1));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(svg.width * px);
      canvas.height = Math.round(svg.height * px);
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      exportCanvasPng(canvas, `relationship-map-${tenantId}-${fileStamp()}.png`);
      URL.revokeObjectURL(url);
    };
    img.onerror = () => URL.revokeObjectURL(url);
    img.src = url;
  }, [graph, tenantId, tenantName, scope.description, scopeSub]);

  const doCsv = useCallback(() => {
    if (!edgeRows.length) return;
    exportCsv(`relationship-edges-${tenantId}-${fileStamp()}.csv`, edgeRows, [
      { label: 'Source', value: 'source' },
      { label: 'Source Type', value: 'sourceType' },
      { label: 'Relation', value: 'relation' },
      { label: 'Target', value: 'target' },
      { label: 'Target Type', value: 'targetType' },
    ]);
  }, [edgeRows, tenantId]);

  const doPdf = useCallback(() => {
    if (!graph) return;
    printElementToPdf(PRINT_ID, `Primordia — Relationship Map — ${tenantName}`);
  }, [graph, tenantName]);

  // ---- Loading / empty -------------------------------------------------------
  if (!sheets) {
    return (
      <Box sx={{ p: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={22} />
        <Typography sx={{ color: GREY }}>Loading the relationship model…</Typography>
      </Box>
    );
  }

  const sheetOptions = full.rows;
  const activeView = savedViews.find((v) => v.id === viewId);

  // ---- Legend (on-canvas, and mirrored into the PDF) -------------------------
  const legendBlock = graph ? (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute', left: 10, bottom: 10, zIndex: 6, p: 1.25,
        maxWidth: 420, maxHeight: '52%', overflowY: 'auto', bgcolor: 'rgba(255,255,255,0.96)',
        border: '1px solid #d7dee6',
      }}
    >
      <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 12, mb: 0.5 }}>Legend</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
        {graph.typeRows.map((t) => (
          <Chip
            key={t.key}
            size="small"
            label={`${t.badge} ${t.label} (${t.count})`}
            sx={{ bgcolor: t.color, color: '#fff', height: 20, fontSize: 10.5, fontWeight: 600 }}
          />
        ))}
      </Box>
      <Stack spacing={0.4}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 32, height: 0, borderTop: `3px solid ${CHAIN_COLOR}` }} />
          <Typography sx={{ fontSize: 11, color: NAVY }}>
            Application → Deployment → IT Component (deployment chain)
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ width: 32, height: 0, borderTop: `1.5px solid ${PLAIN_COLOR}` }} />
          <Typography sx={{ fontSize: 11, color: NAVY }}>Other model relation, labelled with its type</Typography>
        </Stack>
        <Typography sx={{ fontSize: 10.5, color: GREY }}>
          Arrows show the direction the relation is recorded in. Filled nodes are the fact sheets the
          scope started from; outlined nodes were reached by traversal.
        </Typography>
        {!graph.labels && (
          <Typography sx={{ fontSize: 10.5, color: '#8a5a00' }}>
            {`Edge labels are hidden above ${EDGE_LABEL_LIMIT} relations — export the CSV edge list for the full detail.`}
          </Typography>
        )}
      </Stack>
    </Paper>
  ) : null;

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: HEAD }}>Relationship Map</Typography>
        <Chip
          size="small"
          label={`${scope.nodeIds.length.toLocaleString()} nodes · ${scope.edges.length.toLocaleString()} relations in scope`}
          sx={{ bgcolor: '#e8f0fe', color: NAVY, fontWeight: 700 }}
        />
        {danglingInScope.length > 0 && (
          <Chip
            size="small"
            label={`${danglingInScope.length} dangling relation${danglingInScope.length === 1 ? '' : 's'}`}
            sx={{ bgcolor: '#fff3e0', color: '#8a5a00', fontWeight: 700, border: '1px solid #f0c98a' }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="contained" startIcon={<PictureAsPdfIcon />} disabled={!graph}
          onClick={doPdf} sx={{ bgcolor: NAVY, '&:hover': { bgcolor: BLUE } }}>
          PDF
        </Button>
        <Button size="small" variant="outlined" startIcon={<ImageIcon />} disabled={!graph} onClick={doPng}>
          PNG
        </Button>
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />} disabled={!edgeRows.length} onClick={doCsv}>
          CSV edge list
        </Button>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        {/* ---------------- Scope picker ---------------- */}
        <Paper sx={{ p: 1.5, width: { xs: '100%', md: 330 }, flexShrink: 0 }}>
          <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13, mb: 1 }}>Scope</Typography>
          <ToggleButtonGroup
            size="small"
            exclusive
            fullWidth
            value={mode}
            onChange={(e, v) => { if (v) setMode(v); }}
            sx={{ mb: 1.5 }}
          >
            <ToggleButton value="sheet" sx={{ fontSize: 11, textTransform: 'none' }}>Fact sheet</ToggleButton>
            <ToggleButton value="type" sx={{ fontSize: 11, textTransform: 'none' }}>Type</ToggleButton>
            <ToggleButton value="view" sx={{ fontSize: 11, textTransform: 'none' }}>Saved view</ToggleButton>
          </ToggleButtonGroup>

          {mode === 'sheet' && (
            <Autocomplete
              size="small"
              options={sheetOptions}
              value={focusSheet || null}
              getOptionLabel={(o) => (o && o.name) || ''}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              onChange={(e, v) => setFocusId(v ? v.id : '')}
              filterOptions={(options, state) => {
                const q = state.inputValue.trim().toLowerCase();
                const hits = q ? options.filter((o) => o.name.toLowerCase().includes(q)) : options;
                return hits.slice(0, 60);
              }}
              renderOption={(props, o) => (
                <li {...props} key={o.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{
                      width: 20, height: 14, borderRadius: '3px', bgcolor: typeMeta(o.type).color,
                      color: '#fff', fontSize: 9, fontWeight: 700, textAlign: 'center', lineHeight: '14px',
                    }}>
                      {typeMeta(o.type).badge}
                    </Box>
                    <span style={{ fontSize: 13 }}>{o.name}</span>
                  </Stack>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Focus fact sheet" placeholder="Search fact sheets…" />
              )}
            />
          )}

          {mode === 'type' && (
            <TextField
              select fullWidth size="small" label="Fact sheet type"
              value={scopeType} onChange={(e) => setScopeType(e.target.value)}
            >
              {FACT_SHEET_TYPES.map((t) => {
                const n = full.rows.filter((fs) => fs.type === t.key).length;
                return (
                  <MenuItem key={t.key} value={t.key} disabled={n === 0}>
                    {`${t.label} (${n})`}
                  </MenuItem>
                );
              })}
            </TextField>
          )}

          {mode === 'view' && (
            savedViews.length ? (
              <TextField
                select fullWidth size="small" label="Saved view"
                value={viewId} onChange={(e) => setViewId(e.target.value)}
                helperText={activeView ? `${activeView.type} · ${(activeView.filters || []).length} filter(s)` : 'Pick a saved view'}
              >
                {savedViews.map((v) => <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>)}
              </TextField>
            ) : (
              <Alert severity="info" sx={{ fontSize: 12 }}>
                No saved views in this workspace yet. Build one in Saved Views &amp; Reports, then come back —
                it becomes a scope here.
              </Alert>
            )
          )}

          <TextField
            select fullWidth size="small" label="Degrees of separation" value={degrees}
            onChange={(e) => setDegrees(Number(e.target.value))} sx={{ mt: 1.5 }}
            helperText="How far to walk out from the scope. 3 is the maximum — beyond that everything connects to everything."
          >
            <MenuItem value={0}>0 — only the fact sheets in scope</MenuItem>
            <MenuItem value={1}>1 degree (default)</MenuItem>
            <MenuItem value={2}>2 degrees</MenuItem>
            <MenuItem value={3}>3 degrees</MenuItem>
          </TextField>

          <Divider sx={{ my: 1.5 }} />

          <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13, mb: 1 }}>Layout</Typography>
          <ToggleButtonGroup
            size="small" exclusive fullWidth value={layout}
            onChange={(e, v) => { if (v) setLayout(v); }}
          >
            <ToggleButton value="layered" sx={{ fontSize: 11, textTransform: 'none' }}>
              <AccountTreeIcon sx={{ fontSize: 15, mr: 0.5 }} /> Hierarchical
            </ToggleButton>
            <ToggleButton value="radial" sx={{ fontSize: 11, textTransform: 'none' }}>
              <HubIcon sx={{ fontSize: 15, mr: 0.5 }} /> Radial
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography sx={{ fontSize: 11, color: GREY, mt: 0.75 }}>
            Hierarchical reads a dependency chain (Application → Deployment → IT Component). Radial reads a
            hub and its spokes.
          </Typography>

          <Divider sx={{ my: 1.5 }} />

          <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 13, mb: 1 }}>PDF pagination</Typography>
          <TextField
            select fullWidth size="small" value={pageMode} onChange={(e) => setPageMode(e.target.value)}
            helperText={fit
              ? (pageMode === 'fit'
                ? `Scales to ${fit.pct}% on one landscape page — the scale factor is printed on the page.`
                : `Tiles at 100% across ${fit.cols * fit.rows} page(s) (${fit.cols} × ${fit.rows}), each page captioned.`)
              : 'Choose a scope to see the page count.'}
          >
            <MenuItem value="fit">Scale to fit one landscape page</MenuItem>
            <MenuItem value="tile">Full size, tiled across pages</MenuItem>
          </TextField>

          <Divider sx={{ my: 1.5 }} />
          <Typography sx={{ fontSize: 11.5, color: GREY }}>
            <b style={{ color: NAVY }}>Scope:</b> {scope.description}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: GREY, mt: 0.5 }}>
            <b style={{ color: NAVY }}>Counts:</b> {scope.nodeIds.length.toLocaleString()} fact sheets,{' '}
            {scope.edges.length.toLocaleString()} relations
            {scope.seedCount ? `, from ${scope.seedCount.toLocaleString()} seed fact sheet(s)` : ''}
          </Typography>
        </Paper>

        {/* ---------------- Canvas + states ---------------- */}
        <Box sx={{ flex: 1, width: '100%', minWidth: 0 }}>
          {!scope.ready && (
            <Alert severity="info">
              <AlertTitle sx={{ fontWeight: 700 }}>Choose a scope</AlertTitle>
              The map is drawn for a focus fact sheet, a whole fact-sheet type, or a saved view — never for the
              whole tenant at once. This workspace holds {full.rows.length.toLocaleString()} fact sheets and{' '}
              {full.edges.length.toLocaleString()} relations.
            </Alert>
          )}

          {scope.ready && scope.nodeIds.length > 0 && scope.edges.length === 0 && (
            <Alert severity="warning">
              <AlertTitle sx={{ fontWeight: 700 }}>Nothing to map — no relations recorded</AlertTitle>
              {mode === 'sheet' && focusSheet
                ? `${focusSheet.name} has no relations to any other fact sheet, so there is no map to draw. Add them in the Relationship Wizard, or on the fact sheet itself under "Relations & their attributes".`
                : `None of the ${scope.nodeIds.length.toLocaleString()} fact sheets in this scope reference each other or anything else. Add relations in the Relationship Wizard, or on each fact sheet under "Relations & their attributes".`}
            </Alert>
          )}

          {scope.ready && overLimit && !force && scope.edges.length > 0 && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={<Button color="inherit" size="small" onClick={() => setForce(true)}>Render anyway</Button>}
            >
              <AlertTitle sx={{ fontWeight: 700 }}>Too large to draw automatically</AlertTitle>
              {`This scope is ${scope.nodeIds.length.toLocaleString()} fact sheets and ${scope.edges.length.toLocaleString()} relations — above the ${AUTO_RENDER_LIMIT}-node safety limit. Rendering it will be slow and the printed labels will be small. Reduce the degrees of separation, pick a narrower scope, or render it anyway.`}
            </Alert>
          )}

          {danglingInScope.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle sx={{ fontWeight: 700 }}>
                {`Data quality: ${danglingInScope.length} relation${danglingInScope.length === 1 ? '' : 's'} point at fact sheets that no longer exist`}
              </AlertTitle>
              <Typography sx={{ fontSize: 12.5, color: GREY, mb: 0.5 }}>
                These are not drawn because there is nothing to draw them to. They are listed here (and in the PDF)
                because a broken reference is a finding, not noise.
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 3, fontSize: 12 }}>
                {danglingInScope.slice(0, 15).map((d) => (
                  <li key={d.key}>
                    <b>{d.sourceName}</b> — {relLabel(d.relKey)} → missing id <code>{d.missingId}</code>
                    {d.missingName ? ` (recorded as "${d.missingName}")` : ''}
                  </li>
                ))}
              </Box>
              {danglingInScope.length > 15 && (
                <Typography sx={{ fontSize: 12, color: GREY, mt: 0.5 }}>
                  {`…and ${danglingInScope.length - 15} more.`}
                </Typography>
              )}
            </Alert>
          )}

          {graph && (
            <Paper sx={{ height: 640, width: '100%', overflow: 'hidden', position: 'relative' }}>
              <ReactFlow
                key={`${layout}:${graph.nodes.length}:${graph.edges.length}:${mode}:${focusId}`}
                nodes={flow.nodes}
                edges={flow.edges}
                fitView
                minZoom={0.05}
                maxZoom={2}
                nodesDraggable={false}
                nodesConnectable={false}
                elementsSelectable
                onlyRenderVisibleElements={graph.nodes.length > VIRTUALISE_ABOVE}
                zoomOnScroll={false}
                panOnScroll={false}
                preventScrolling={false}
                onNodeClick={(_, node) => { if (typeof onOpen === 'function') onOpen(node.id); }}
                onNodeDoubleClick={(_, node) => { setMode('sheet'); setFocusId(node.id); }}
                proOptions={{ hideAttribution: true }}
              >
                <Background gap={18} color="#eef1f5" />
                <Controls showInteractive={false} />
              </ReactFlow>
              {legendBlock}
            </Paper>
          )}

          {graph && (
            <Typography sx={{ fontSize: 11.5, color: GREY, mt: 1 }}>
              Click a node to open its fact sheet · double-click to re-centre the map on it.
              {fit && pageMode === 'fit' && ` PDF will print at ${fit.pct}% of full size on one landscape page.`}
              {fit && pageMode === 'tile' && ` PDF will print full size across ${fit.cols * fit.rows} landscape page(s).`}
            </Typography>
          )}
        </Box>
      </Stack>

      {/* ---------------- Hidden print block (the PDF payload) ---------------- */}
      <div id={PRINT_ID} style={{ display: 'none' }}>
        <style>{PRINT_CSS}</style>
        {graph && printSvg && fit && (
          <>
            <div className="rm-title">
              <PrimordiaLogo size={30} />
              <div>
                <h1>Primordia — Relationship Map</h1>
                <div className="sub">{tenantName}</div>
              </div>
            </div>
            <div className="rm-meta">
              <span><b>Scope:</b> {scope.description}</span>
              <span><b>Nodes:</b> {graph.nodes.length.toLocaleString()}</span>
              <span><b>Relations:</b> {graph.edges.length.toLocaleString()}</span>
              <span><b>Layout:</b> {layout === 'layered' ? 'Hierarchical (layered)' : 'Radial'}</span>
              <span><b>Generated:</b> {nowStamp()}</span>
              <span><b>Workspace:</b> {tenantId}</span>
            </div>
            <div className="rm-note">
              {pageMode === 'fit'
                ? `The map follows on its own landscape page, scaled to ${fit.pct}% of full size (nothing is cropped). Page layout assumes Letter or A4 landscape with 10 mm margins.`
                : `The map follows at 100%, tiled across ${fit.cols * fit.rows} landscape page(s) in a ${fit.cols} × ${fit.rows} grid — read left to right, top to bottom. Nothing is cropped.`}
              {!graph.labels && ` Edge labels are omitted above ${EDGE_LABEL_LIMIT} relations; the relation type of every edge is in the edge list below.`}
            </div>

            <div className="rm-legend">
              <h2>Legend</h2>
              <div className="items">
                {graph.typeRows.map((t) => (
                  <span className="it" key={t.key}>
                    <span className="bg" style={{ background: t.color }}>{t.badge}</span>
                    <span>{t.label} ({t.count})</span>
                  </span>
                ))}
              </div>
              <div style={{ marginTop: 6 }}>
                <div>
                  <span className="rule" style={{ borderTop: `3px solid ${CHAIN_COLOR}` }} />
                  &nbsp;→&nbsp; Application → Deployment → IT Component (deployment chain)
                </div>
                <div>
                  <span className="rule" style={{ borderTop: `1.5px solid ${PLAIN_COLOR}` }} />
                  &nbsp;→&nbsp; Other model relation, labelled with its relation type
                </div>
                <div style={{ color: GREY, marginTop: 4 }}>
                  Arrows show the direction the relation is recorded in. Filled nodes are the fact sheets the scope
                  started from; outlined nodes were reached by traversal.
                </div>
              </div>
            </div>

            {pageMode === 'fit' ? (
              <div className="rm-page rm-mappage">
                <div
                  dangerouslySetInnerHTML={{
                    __html: svgDoc(printSvg, printSvg.width * fit.scale, printSvg.height * fit.scale),
                  }}
                />
                <div className="rm-cap">
                  {`${tenantName} — ${scope.description} — whole map on one page at ${fit.pct}% of full size, nothing cropped.`}
                </div>
              </div>
            ) : (
              Array.from({ length: fit.cols * fit.rows }).map((_, i) => {
                const c = i % fit.cols;
                const r = Math.floor(i / fit.cols);
                return (
                  <div className="rm-page rm-mappage" key={`tile-${r}-${c}`}>
                    <div
                      className="rm-tile"
                      style={{ width: PAGE_W, height: MAP_H }}
                      dangerouslySetInnerHTML={{
                        __html: `<div style="left:${-c * PAGE_W}px;top:${-r * MAP_H}px">${svgDoc(printSvg, printSvg.width, printSvg.height)}</div>`,
                      }}
                    />
                    <div className="rm-cap">
                      {`Map tile row ${r + 1} of ${fit.rows}, column ${c + 1} of ${fit.cols} — ${tenantName} — ${scope.description} — 100% scale`}
                    </div>
                  </div>
                );
              })
            )}

            {danglingInScope.length > 0 && (
              <div className="rm-note">
                <b>{`Data quality: ${danglingInScope.length} relation(s) in this scope point at fact sheets that no longer exist.`}</b>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {danglingInScope.slice(0, 40).map((d) => (
                    <li key={`p-${d.key}`}>
                      {`${d.sourceName} — ${relLabel(d.relKey)} → missing id ${d.missingId}${d.missingName ? ` (recorded as "${d.missingName}")` : ''}`}
                    </li>
                  ))}
                </ul>
                {danglingInScope.length > 40 && <div>{`…and ${danglingInScope.length - 40} more.`}</div>}
              </div>
            )}

            <h2 style={{ fontSize: 13, color: NAVY, margin: '12px 0 6px' }}>
              {`Relation edge list${edgeRows.length > EDGE_TABLE_LIMIT ? ` — first ${EDGE_TABLE_LIMIT} of ${edgeRows.length}` : ` — all ${edgeRows.length}`}`}
            </h2>
            {edgeRows.length > EDGE_TABLE_LIMIT && (
              <div className="rm-cap" style={{ marginBottom: 4 }}>
                The complete list is in the CSV export (Relationship Map → CSV edge list); nothing has been dropped
                from the map itself.
              </div>
            )}
            <table className="rm-edges">
              <thead>
                <tr>
                  <th>Source</th><th>Source Type</th><th>Relation</th><th>Target</th><th>Target Type</th>
                </tr>
              </thead>
              <tbody>
                {edgeRows.slice(0, EDGE_TABLE_LIMIT).map((r) => (
                  <tr key={`pe-${r.id}`}>
                    <td>{r.source}</td><td>{r.sourceType}</td><td>{r.relation}</td>
                    <td>{r.target}</td><td>{r.targetType}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* ---------------- On-screen edge list ---------------- */}
      {graph && (
        <Paper sx={{ p: 1.5, mt: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
            <Typography sx={{ fontWeight: 700, color: HEAD, fontSize: 14 }}>Edge list</Typography>
            <Chip size="small" label={`${edgeRows.length} relations`} sx={{ height: 20, fontSize: 11, bgcolor: '#eef3f9', color: NAVY, fontWeight: 700 }} />
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: 11.5, color: GREY }}>
              {edgeRows.length > EDGE_TABLE_LIMIT
                ? `Showing the first ${EDGE_TABLE_LIMIT} — export the CSV for all ${edgeRows.length}.`
                : 'Every relation drawn on the map above.'}
            </Typography>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, color: HEAD, fontSize: 12 } }}>
                <TableCell>Source</TableCell>
                <TableCell>Source Type</TableCell>
                <TableCell>Relation</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Target Type</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {edgeRows.slice(0, EDGE_TABLE_LIMIT).map((r) => (
                <TableRow key={r.id}>
                  <TableCell sx={{ fontSize: 12 }}>{r.source}</TableCell>
                  <TableCell sx={{ fontSize: 12, color: GREY }}>{r.sourceType}</TableCell>
                  <TableCell sx={{ fontSize: 12, color: BLUE }}>{r.relation}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{r.target}</TableCell>
                  <TableCell sx={{ fontSize: 12, color: GREY }}>{r.targetType}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
